import IneligibleHoursTooltip from "./IneligibleHoursTooltip";
import { TipBreakdownTable } from "./TipJobHours";
import React from "react";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import CheckIcon from "@mui/icons-material/Check";
import SyncIcon from "@mui/icons-material/Sync";
import DownloadIcon from "@mui/icons-material/Download";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { formatName } from "../../../helpers/helpers";
import OperationsPageHeader from "../home/top_bar_components/OperationsPageHeader";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import RightworkDatePicker from "../../../components/shared/RightworkDatePicker";
import ConfirmDialog from "../boards/ConfirmDialog";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { getStoreBusinessDate } from "../../../utilities/storeTime";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerSmall from "../requests/shared/helpers/DrawerFooterContainerSmall";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";
import ButtonFilled from "../requests/shared/helpers/ButtonFilled";
import TipsExportModal from "./TipsExportModal";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_confirm_tip_closeout,
  API_delete_tip_adjustment,
  API_get_tip_close_summary,
  API_get_tip_employee_detail,
  API_get_tip_store_breakdown,
  API_recompute_tips,
  API_reopen_tip_closeout,
  API_save_tip_adjustment,
} from "../../../constants";

// Time & Payroll > Tips. The Monday close: every tip-pooling location for a
// date range with its Toast totals, where the money went, and what still
// blocks confirming; then a per-location breakdown of who contributed and
// received what, with a per-person drawer and manager adjustments.

const CLASSES = ["tips", "service_charge", "declared_cash"];
const CLASS_LABEL = { tips: "Tips", service_charge: "Service charge", declared_cash: "Declared cash" };
const SOURCE_LABEL = {
  gratuity_service_charge: "Service charges",
  credit_card_tip: "Credit card tips",
  gift_card_tip: "Gift card tips",
  declared_cash_tip: "Declared cash",
  online_order_tip: "Online ordering",
};
const WEEKDAY = { monday: 0, tuesday: 1, wednesday: 2, thursday: 3, friday: 4, saturday: 5, sunday: 6 };
const STATUS = {
  confirmed: { label: "Confirmed", cls: "bg-emerald-50 border-emerald-200 text-emerald-800", dot: "bg-emerald-600" },
  needs_attention: { label: "Needs attention", cls: "bg-amber-50 border-amber-200 text-amber-800", dot: "bg-amber-600" },
  ready: { label: "Ready to confirm", cls: "bg-slate-50 border-slate-200 text-slate-700", dot: "bg-slate-500" },
  no_policy: { label: "No policy", cls: "bg-slate-100 border-slate-200 text-slate-600", dot: "bg-slate-400" },
};

export const dollars = (cents, { sign = false } = {}) => {
  const value = Math.abs(Number(cents) || 0) / 100;
  const text = `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if ((Number(cents) || 0) < 0) return `−${text}`;
  return sign && cents > 0 ? `+${text}` : text;
};
const sumClasses = (amounts) => CLASSES.reduce((total, key) => total + (Number(amounts?.[key]) || 0), 0);
// Checks the server refuses to confirm over (not warnings a manager may
// accept): the card shows the reason instead of a button that would fail.
const HARD_BLOCKS = ["stale_draft", "no_runs", "missing_runs", "pool_added", "overlapping_close"];
const hardBlock = (row) => (row?.checks || []).find((check) => !check.ok && HARD_BLOCKS.includes(check.code));
const iso = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
const parseIso = (text) => {
  const [y, m, d] = String(text).split("-").map(Number);
  return new Date(y, m - 1, d);
};
const readable = (text) => parseIso(text).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const shortDate = (text) => parseIso(text).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

// Business weeks start on the store's configured day; "last week" is the most
// recent complete one, which is what payroll asks for on Monday morning.
export const rangeForPreset = (preset, weekStartIndex, custom, today = new Date()) => {
  if (preset === "custom") return { start: custom.start, end: custom.end };
  const jsDay = (today.getDay() + 6) % 7; // Monday = 0
  const offset = (jsDay - weekStartIndex + 7) % 7;
  const thisStart = addDays(today, -offset);
  if (preset === "this_week") return { start: iso(thisStart), end: iso(addDays(thisStart, 6)) };
  const lastStart = addDays(thisStart, -7);
  return { start: iso(lastStart), end: iso(addDays(lastStart, 6)) };
};

const SYNC_POLL_MS = 2000;
const SYNC_IN_PROGRESS = ["queued", "running"];
const SYNC_FAILED_MESSAGE = "Toast sync did not finish. Please try again.";
const payoutNote = (changed) => (changed === true ? "Some payout amounts changed." : changed === false ? "No payout amounts changed." : "");
const clockTime = (text) => new Date(text).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const dayAndTime = (text) => new Date(text).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// A Sync runs on the server for up to about 20 minutes and nothing on the
// page moves until it ends, so its state stays on screen, not in a toast.
const SyncStatus = ({ active, last, className = "" }) => {
  if (active) {
    const dates = `${shortDate(active.start_date)} to ${shortDate(active.end_date)}`;
    return (
      <div role="status" className={`flex items-start gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3.5 py-2.5 text-[13px] text-violet-900 ${className}`}>
        <SyncIcon style={{ fontSize: 16 }} className="mt-0.5 animate-spin" />
        <span>
          {active.status === "queued"
            ? `Waiting to start the Toast sync for ${dates}. Syncs run one at a time, so this can take a while.`
            : `Syncing ${dates} from Toast${active.started_at ? ` since ${clockTime(active.started_at)}` : ""}. This can take up to about 20 minutes, and the numbers update when it finishes.`}
          {" "}You can leave this page.
        </span>
      </div>
    );
  }
  if (!last) return null;
  return <p className={`text-xs text-slate-500 ${className}`}>Last synced {dayAndTime(last.completed_at)}. {payoutNote(last.amounts_changed)}</p>;
};

const Badge = ({ status }) => {
  const config = STATUS[status] || STATUS.ready;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border py-0.5 pl-2 pr-2.5 text-[11px] font-semibold leading-4 ${config.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />{config.label}
    </span>
  );
};

const Kpi = ({ label, value, detail, tone }) => (
  <div className={`rounded-xl border p-3.5 ${tone === "warn" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
    <div className={`text-xs ${tone === "warn" ? "text-amber-800" : "text-slate-500"}`}>{label}</div>
    <div className={`mt-1 text-xl font-semibold tabular-nums ${tone === "warn" ? "text-amber-800" : "text-slate-900"}`}>{value}</div>
    {detail && <div className={`mt-0.5 text-xs ${tone === "warn" ? "text-amber-800" : "text-slate-500"}`}>{detail}</div>}
  </div>
);

const Money = ({ label, cents, strong, sub }) => (
  <div className="flex items-baseline justify-between py-1">
    <span className={`text-[13px] ${strong ? "text-slate-900" : "text-slate-500"}`}>{label}{sub && <span className="ml-1 text-xs text-slate-400">{sub}</span>}</span>
    <span className={`text-[13px] tabular-nums ${strong ? "font-semibold text-slate-900" : "text-slate-900"}`}>{dollars(cents)}</span>
  </div>
);

// Header actions, the Tickets shapes: 42px to sit level with the range select.
const btnPrimary = "flex h-[42px] items-center gap-2 rounded-lg bg-violet-700 px-4 text-sm font-medium text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50";
const btnOutline = "flex h-[42px] items-center gap-2 rounded-lg border border-violet-700 bg-white px-4 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50";
const btnQuiet = "flex h-[42px] items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50";
const chip = (active) => `rounded-full px-3 py-1 text-xs font-semibold ${active ? "bg-violet-50 text-violet-800" : "text-slate-600 hover:bg-slate-100"}`;
const inputSx = { backgroundColor: "white", borderRadius: "8px", height: "42px", "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" } };

export default function TipsDisplay(props) {
  const homeStoreInfo = props.storeNames?.find((store) => Number(store.id) === Number(props.homeStore));
  const companyId = homeStoreInfo?.company || props.homeCompany;
  const weekStartIndex = WEEKDAY[String(homeStoreInfo?.business_week_start_day || "monday").toLowerCase()] ?? 0;
  const tipStores = React.useMemo(
    () => (props.storeNames || []).filter((store) => Number(store.company) === Number(companyId) && store.tip_pooling_enabled),
    [props.storeNames, companyId]
  );
  // The header's company dropdown offers only companies with a location
  // pooling tips, like Contests does for its own flag.
  const tipCompanyIds = React.useMemo(
    () => [...new Set((props.storeNames || []).filter((store) => store.tip_pooling_enabled).map((store) => store.company))],
    [props.storeNames]
  );

  const [preset, setPreset] = React.useState("last_week");
  // "Today" is the home store's business date: at 1am on a 4am-start store
  // the week has not turned yet, and a viewer in another zone sees the
  // store's calendar, not their own.
  const storeToday = getStoreBusinessDate(homeStoreInfo?.timezone, homeStoreInfo?.start_qhnum);
  const [custom, setCustom] = React.useState(() => rangeForPreset("last_week", weekStartIndex, {}, parseIso(storeToday)));
  const range = React.useMemo(() => rangeForPreset(preset, weekStartIndex, custom, parseIso(storeToday)), [preset, weekStartIndex, custom, storeToday]);
  // Overview is the page; a location card opens its breakdown as a drill-in.
  const [view, setView] = React.useState("close");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [query, setQuery] = React.useState("");
  const [summary, setSummary] = React.useState(null);
  const [summaryFailed, setSummaryFailed] = React.useState(false);
  const [breakdownStoreId, setBreakdownStoreId] = React.useState(Number(props.homeStore));
  // Switching the header location to another company leaves the old
  // location id behind; snap to the home location or the first pooling one.
  // Only locations this manager can act on; until the summary answers, the
  // company's pooling locations stand in.
  const manageableStores = React.useMemo(
    () => (summary?.stores ? summary.stores.filter((store) => store.can_manage !== false).map((store) => ({ id: store.store_id })) : tipStores),
    [summary, tipStores]
  );
  React.useEffect(() => {
    if (!manageableStores.length || manageableStores.some((store) => Number(store.id) === Number(breakdownStoreId))) return;
    const home = manageableStores.find((store) => Number(store.id) === Number(props.homeStore));
    setBreakdownStoreId(Number((home || manageableStores[0]).id));
  }, [manageableStores, breakdownStoreId, props.homeStore]);
  const [breakdown, setBreakdown] = React.useState(null);
  const breakdownRef = React.useRef(null);
  breakdownRef.current = breakdown;
  const [breakdownFailed, setBreakdownFailed] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const [detailFailed, setDetailFailed] = React.useState(false);
  const [adjustment, setAdjustment] = React.useState(null);
  // The column shows whenever anyone in the range was adjusted in any
  // class, not only when the adjustments fail to net to zero across people
  // or across classes (a +$10 tips and -$10 service charge pair is two
  // changed take-home amounts).
  const anyClass = (amounts) => CLASSES.some((key) => (Number(amounts?.[key]) || 0) !== 0);
  const hasAdjustments = Boolean(breakdown) && (
    anyClass(breakdown.totals?.adjustments) || (breakdown.rows || []).some((row) => anyClass(row.adjustments))
  );
  // Declared cash is part of Take home wherever a pool distributes it, so
  // it gets its own column then; the visible parts always add up.
  const cashOf = (amounts) => Number(amounts?.declared_cash) || 0;
  const hasCash = Boolean(breakdown) && (
    cashOf(breakdown.totals?.received) !== 0 || (breakdown.rows || []).some((row) => cashOf(row.received) !== 0)
  );
  const detailHasCash = (detail?.runs || []).some((run) => cashOf(run.amounts) !== 0);
  const [pendingReopen, setPendingReopen] = React.useState(null);
  // The tips file for the whole company, or (from a location's breakdown)
  // opened on that one location; the modal can widen or narrow either.
  const [exportOpen, setExportOpen] = React.useState(false);
  const [exportStoreIds, setExportStoreIds] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  // Syncs in progress by location. Each comes from the button or from the
  // server's summary, so one started earlier is still shown after a reload.
  const [activeSyncs, setActiveSyncs] = React.useState({});
  const finishedSyncs = React.useRef(new Set());
  const adoptSyncs = React.useCallback((rows) => {
    setActiveSyncs((current) => {
      const adopted = rows.filter((row) => row?.sync?.active && !current[row.store_id] && !finishedSyncs.current.has(row.sync.active.refresh_id));
      if (!adopted.length) return current;
      const next = { ...current };
      adopted.forEach((row) => { next[row.store_id] = { ...row.sync.active, store_name: row.store_name, company_id: companyId }; });
      return next;
    });
  }, [companyId]);
  // One ticket per request kind: a slow answer for a range or location the
  // manager has since moved away from must not replace the current one.
  const requested = React.useRef(0);
  const breakdownTicket = React.useRef(0);
  const detailTicket = React.useRef(0);

  // A weekly preset goes to the server by name: each location resolves it
  // to its own business week (its week start day, its business date), and
  // its card and breakdown come back with the dates it actually used.
  const rangeParams = React.useMemo(() => ({
    company_id: companyId, start_date: range.start, end_date: range.end, ...(preset === "custom" ? {} : { preset }),
  }), [companyId, range, preset]);
  // The dates a location's actions apply to: what its summary or breakdown
  // reported, else the page range.
  const datesOf = React.useCallback((row) => ({ start: row?.start_date || range.start, end: row?.end_date || range.end }), [range]);

  const loadSummary = React.useCallback(async () => {
    if (!companyId || !range.start || !range.end) return;
    const ticket = ++requested.current;
    setSummaryFailed(false);
    try {
      const res = await getRightworkAPI(API_get_tip_close_summary, rangeParams);
      if (ticket !== requested.current) return;
      setSummary(res.data?.redux?.payload || null);
      adoptSyncs(res.data?.redux?.payload?.stores || []);
    } catch (_) {
      if (ticket === requested.current) setSummaryFailed(true);
    }
  }, [companyId, range, rangeParams, adoptSyncs]);

  const loadBreakdown = React.useCallback(async (storeId = breakdownStoreId) => {
    if (!companyId || !storeId) return;
    const ticket = ++breakdownTicket.current;
    setBreakdownFailed(false);
    try {
      const res = await getRightworkAPI(API_get_tip_store_breakdown, { ...rangeParams, store_id: storeId });
      if (ticket !== breakdownTicket.current) return;
      setBreakdown(res.data?.redux?.payload || null);
      adoptSyncs([res.data?.redux?.payload]);
    } catch (_) {
      // The previous table must not stay up beside the error: after a
      // confirm or reopen whose refresh failed it would show stale controls.
      if (ticket === breakdownTicket.current) { setBreakdown(null); setBreakdownFailed(true); }
    }
  }, [companyId, breakdownStoreId, rangeParams, adoptSyncs]);

  const loadDetail = React.useCallback(async (storeId, storeTeamMemberId) => {
    const ticket = ++detailTicket.current;
    setDetailFailed(false);
    try {
      const res = await getRightworkAPI(API_get_tip_employee_detail, {
        company_id: companyId, start_date: datesOf(breakdownRef.current).start, end_date: datesOf(breakdownRef.current).end, store_id: storeId, store_team_member_id: storeTeamMemberId,
      });
      if (ticket !== detailTicket.current) return;
      setDetail(res.data?.redux?.payload || null);
    } catch (_) {
      if (ticket === detailTicket.current) setDetailFailed(true);
    }
  }, [companyId, datesOf]);

  React.useEffect(() => { setSummary(null); loadSummary(); }, [loadSummary]);
  // A range change closes the open drawer: a person's runs for last week
  // must not sit under this week's heading with this week's adjustment
  // form, and the new breakdown's own dates are not known until it loads.
  const detailPerson = React.useRef(null);
  detailPerson.current = detail && !detail.loading ? detail.store_team_member_id : detailPerson.current;
  React.useEffect(() => {
    if (!detailPerson.current) return;
    detailTicket.current += 1;
    detailPerson.current = null;
    setAdjustment(null);
    setDetail(null);
  }, [loadDetail]);  // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (view !== "breakdown") {
      breakdownTicket.current += 1;
      return;
    }
    setBreakdown(null);
    loadBreakdown();
  }, [view, loadBreakdown]);

  const refreshAll = async () => {
    await loadSummary();
    if (view === "breakdown") await loadBreakdown();
  };
  // An action that finishes after the manager moved to another range or
  // location must refresh what is on screen now, not what was there when it
  // started; the ref always points at the latest loaders.
  const refreshRef = React.useRef(refreshAll);
  refreshRef.current = refreshAll;

  const act = async (url, body, message) => {
    setBusy(true);
    toast.loading("Working...", { autoClose: false });
    try {
      const res = await postRightworkAPI(url, { company_id: companyId, ...body });
      toast.dismiss();
      toast.success(message, { autoClose: 3000 });
      await refreshRef.current();
      return res?.data?.redux?.payload || true;
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.system?.message ?? "Request Failed", { autoClose: 5000 });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const confirmStore = async (row) => {
    const { start, end } = datesOf(row);
    const confirmed = await act(API_confirm_tip_closeout, { store_id: row.store_id, start_date: start, end_date: end, expected_amounts_digest: row.amounts_digest }, `${row.store_name} confirmed for ${readable(start)} to ${readable(end)}`);
    // Refused, perhaps because the amounts moved or someone else's Sync began
    // after this page loaded: the refresh shows the new numbers and that Sync.
    if (!confirmed) await refreshRef.current();
    return confirmed;
  };
  const reopenStore = async () => {
    const target = pendingReopen;
    setPendingReopen(null);
    await act(API_reopen_tip_closeout, { store_id: target.store_id, start_date: target.start_date, end_date: target.end_date }, `${target.store_name} reopened`);
  };
  const syncMounted = React.useRef(true);
  React.useEffect(() => {
    syncMounted.current = true;
    return () => { syncMounted.current = false; };
  }, []);
  React.useEffect(() => { setActiveSyncs({}); }, [companyId]);
  const recompute = async (row) => {
    const { start, end } = datesOf(row);
    setBusy(true);
    try {
      const response = await postRightworkAPI(API_recompute_tips, { company_id: companyId, store_id: row.store_id, start_date: start, end_date: end });
      const sync = response?.data?.redux?.payload;
      if (!SYNC_IN_PROGRESS.includes(sync?.status)) throw new Error(sync?.error || SYNC_FAILED_MESSAGE);
      if (syncMounted.current) setActiveSyncs((current) => ({ ...current, [row.store_id]: { ...sync, store_name: row.store_name, company_id: companyId } }));
    } catch (error) {
      if (syncMounted.current) toast.error(error.response?.data?.system?.message ?? error.message ?? "Toast sync failed", { autoClose: 5000 });
    } finally {
      if (syncMounted.current) setBusy(false);
    }
  };
  // Every Sync in progress is polled from here, whichever way it was found.
  // A poll that fails on the network keeps trying; the job has its own expiry
  // on the server, so this cannot go on forever.
  React.useEffect(() => {
    const entries = Object.values(activeSyncs);
    if (!entries.length) return undefined;
    const timer = setTimeout(async () => {
      const answers = await Promise.all(entries.map(async (entry) => {
        try {
          const response = await getRightworkAPI(API_recompute_tips, { company_id: entry.company_id, store_id: entry.store_id, refresh_id: entry.refresh_id });
          return { entry, sync: response?.data?.redux?.payload };
        } catch (error) {
          return { entry, error };
        }
      }));
      if (!syncMounted.current) return;
      const isDone = ({ sync, error }) => (error ? Boolean(error.response && error.response.status < 500) : !SYNC_IN_PROGRESS.includes(sync?.status));
      const done = answers.filter((answer) => isDone(answer) && !finishedSyncs.current.has(answer.entry.refresh_id));
      done.forEach(({ entry, sync, error }) => {
        finishedSyncs.current.add(entry.refresh_id);
        if (sync?.status === "completed") toast.success(`${entry.store_name} synced from Toast. ${payoutNote(sync.amounts_changed)}`.trim(), { autoClose: 5000 });
        else toast.error(error?.response?.data?.system?.message ?? sync?.error ?? SYNC_FAILED_MESSAGE, { autoClose: 5000 });
      });
      // Actions stay off until the refresh lands: a Sync may have changed the
      // amounts, and nobody should confirm payroll they have not seen yet.
      if (done.length) setBusy(true);
      setActiveSyncs((current) => {
        const next = { ...current };
        answers.forEach((answer) => {
          const { entry, sync } = answer;
          if (next[entry.store_id]?.refresh_id !== entry.refresh_id) return;
          if (isDone(answer)) delete next[entry.store_id];
          else if (sync) next[entry.store_id] = { ...next[entry.store_id], ...sync };
        });
        return next;
      });
      if (done.length) {
        // A partial import can flag old data for review even on failure.
        try {
          await refreshRef.current();
        } finally {
          if (syncMounted.current) setBusy(false);
        }
      }
    }, SYNC_POLL_MS);
    return () => clearTimeout(timer);
  }, [activeSyncs]);
  // While a location syncs, the runs it covers are flagged as a normal part of
  // the work, and that warning is about to clear: the status above it says what
  // is going on. A Sync of other dates will not clear it, so it stays. A request
  // stops at the store's business date, so an open week is covered through today.
  const syncCovers = (sync, row) => {
    const { start, end } = datesOf(row);
    const through = row.today && row.today < end ? row.today : end;
    return sync.start_date <= start && sync.end_date >= through;
  };
  const shownChecks = (row) => {
    const sync = activeSyncs[row.store_id];
    return sync && syncCovers(sync, row) ? (row.checks || []).filter((check) => check.code !== "stale_draft") : row.checks || [];
  };
  // Opening a location from a card far down the page starts its breakdown
  // at the top; coming back lands where the card was.
  const overviewScroll = React.useRef(null);
  const review = (storeId) => {
    overviewScroll.current = window.scrollY;
    // Cleared in the same click as the switch: otherwise the drill-in draws
    // the previously opened location's tiles and table for one frame before
    // the load effect blanks them.
    breakdownTicket.current += 1;
    setBreakdown(null);
    setBreakdownFailed(false);
    setBreakdownStoreId(Number(storeId));
    setView("breakdown");
    window.scrollTo({ top: 0 });
  };
  const backToOverview = () => {
    setView("close");
  };
  React.useEffect(() => {
    if (view !== "close" || overviewScroll.current === null) return;
    window.scrollTo({ top: overviewScroll.current });
    overviewScroll.current = null;
  }, [view]);
  const openPerson = (row) => {
    // Known from the moment the drawer opens, so a range change while the
    // first load is in flight invalidates it like any other.
    detailPerson.current = row.store_team_member_id;
    setDetail({ loading: true, name: formatName(row.name), store_team_member_id: row.store_team_member_id });
    loadDetail(breakdownStoreId, row.store_team_member_id);
  };
  const closePerson = () => { detailTicket.current += 1; detailPerson.current = null; setDetail(null); setAdjustment(null); };
  // After an adjustment saves, reload the drawer only if it is still showing
  // the same person at the same location; a manager who moved on keeps the
  // view they moved to.
  const currentStore = React.useRef(breakdownStoreId);
  currentStore.current = breakdownStoreId;
  const reloadIfStillOpen = (storeId, personId) => {
    if (Number(currentStore.current) !== Number(storeId) || detailPerson.current !== personId) return;
    loadDetailRef.current(storeId, personId);
  };
  const loadDetailRef = React.useRef(loadDetail);
  loadDetailRef.current = loadDetail;
  // A person belongs to one location; switching locations closes their drawer.
  React.useEffect(() => { closePerson(); }, [breakdownStoreId]);  // eslint-disable-line react-hooks/exhaustive-deps
  const saveAdjustment = async () => {
    const cents = Math.round(Number(adjustment.amount) * 100);
    const ok = await act(
      API_save_tip_adjustment,
      { store_id: breakdownStoreId, store_team_member_id: detail.store_team_member_id, business_date: adjustment.business_date, source_class: adjustment.source_class, amount_cents: cents, reason: adjustment.reason },
      "Adjustment added"
    );
    if (ok) {
      setAdjustment(null);
      reloadIfStillOpen(breakdownStoreId, detail.store_team_member_id);
    }
  };
  const removeAdjustment = async (item) => {
    const target = { storeId: breakdownStoreId, personId: detail.store_team_member_id };
    const ok = await act(API_delete_tip_adjustment, { store_id: target.storeId, adjustment_uuid: item.uuid }, "Adjustment removed");
    if (ok) reloadIfStillOpen(target.storeId, target.personId);
  };

  const rangeDays = React.useMemo(() => {
    const days = [];
    // Every day the breakdown covers, including the days a longer run
    // reaches before the requested range: those payouts are reviewed and
    // frozen here too, so a correction must be recordable against them.
    const requested = datesOf(breakdown);
    const start = breakdown?.scope_start_date || requested.start;
    const last = breakdown?.scope_end_date || requested.end;
    if (!start || !last) return days;
    let cursor = parseIso(start);
    const end = parseIso(last);
    while (cursor <= end && days.length < 70) {
      days.push(iso(cursor));
      cursor = addDays(cursor, 1);
    }
    return days;
  }, [datesOf, breakdown]);

  // ---- rendering ----------------------------------------------------------------------

  // The date range is a named preset in the header, each labelled with the
  // dates it resolves to at the home location; Custom opens two pickers
  // under the header.
  const presetDates = (key) => {
    const r = rangeForPreset(key, weekStartIndex, custom, parseIso(storeToday));
    return `${shortDate(r.start)} to ${shortDate(r.end)}`;
  };
  const rangeSelect = (
    <div className="w-full max-w-[280px]">
      <label id="tips-range-label" htmlFor="tips-range" className="sr-only">Date range</label>
      <RightworkSelect id="tips-range" value={preset} onChange={(event) => setPreset(event.target.value)} options={[
        { value: "last_week", label: `Last week · ${presetDates("last_week")}` },
        { value: "this_week", label: `This week · ${presetDates("this_week")}` },
        { value: "custom", label: "Custom range" },
      ]} />
    </div>
  );
  const customPickers = preset === "custom" && (
    <div className="flex flex-wrap items-center gap-2">
      <RightworkDatePicker width={170} value={custom.start} label="Range start" onChange={(date) => setCustom((prev) => ({ ...prev, start: date }))} />
      <span className="text-sm text-slate-500">to</span>
      <RightworkDatePicker width={170} value={custom.end} label="Range end" onChange={(date) => setCustom((prev) => ({ ...prev, end: date }))} />
    </div>
  );
  const exportButton = (
    <button type="button" onClick={() => { setExportStoreIds(null); setExportOpen(true); }} disabled={!range.start || !range.end} className={btnPrimary}>
      <DownloadIcon style={{ fontSize: 18 }} /><span>Export tips file</span>
    </button>
  );

  const checksList = (checks) => (
    <div>
      {checks.map((check) => (
        <div key={check.code} className={`flex items-center gap-2 py-1 text-[13px] ${check.ok ? "text-slate-700" : "text-amber-800"}`}>
          {check.ok ? <CheckIcon style={{ fontSize: 14 }} className="text-emerald-600" /> : <WarningAmberOutlinedIcon style={{ fontSize: 14 }} className="text-amber-600" />}
          <span>{check.text}</span>
        </div>
      ))}
    </div>
  );

  const storeCard = (store) => (
    <section key={store.store_id} aria-label={`${store.store_name} close`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center gap-2.5 border-b border-slate-200 bg-slate-50 px-4 py-3">
        {store.can_manage !== false
          ? <button type="button" onClick={() => review(store.store_id)} className="text-sm font-semibold text-slate-900 hover:text-violet-700">{store.store_name}</button>
          : <span className="text-sm font-semibold text-slate-900">{store.store_name}</span>}
        <Badge status={store.status} />
        {store.policy_name && <span className="text-xs text-slate-500">{store.policy_name}</span>}
        {store.pooling_off && <span className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600" title="Tip pooling is switched off or the location is closed; its tip history is still here to review, confirm, or reopen.">Tip pooling off</span>}
        {store.start_date && (store.start_date !== range.start || store.end_date !== range.end) && (
          <span className="text-xs text-slate-500">{readable(store.start_date)} to {readable(store.end_date)} (this location's week)</span>
        )}
        <span className="flex-1" />
        {store.can_manage !== false && <button type="button" onClick={() => review(store.store_id)} aria-label={`Open ${store.store_name}`} className="text-xs font-medium text-violet-700 hover:underline">Open ›</button>}
        {store.can_manage === false ? null : (store.overlapping_closeouts || []).length > 0 ? (
          <span className="text-xs text-slate-500">Inside a confirmed close ({store.overlapping_closeouts[0].start_date} to {store.overlapping_closeouts[0].end_date}); pick that range to reopen it</span>
        ) : activeSyncs[store.store_id] ? (
          <span className="text-xs text-slate-500">Available when the sync finishes</span>
        ) : store.status === "confirmed" ? (
          <button type="button" disabled={busy} onClick={() => setPendingReopen({ ...store, start_date: datesOf(store).start, end_date: datesOf(store).end })} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Reopen</button>
        ) : store.status !== "no_policy" && hardBlock(store) ? (
          <span className="text-xs text-slate-500">{hardBlock(store).text}</span>
        ) : store.status !== "no_policy" && (
          <button type="button" disabled={busy || store.confirmable === false} title={store.confirmable === false ? "Only past business days can be confirmed; today's punches and sales are still moving." : undefined} onClick={() => confirmStore(store)} className={`rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${store.status === "ready" ? "bg-violet-700 text-white hover:bg-violet-800" : "border border-violet-700 bg-white text-violet-700 hover:bg-violet-50"}`}>
            {store.status === "ready" ? "Confirm" : "Confirm anyway"}
          </button>
        )}
      </div>
      {(activeSyncs[store.store_id] || store.sync?.last) && (
        <div className="border-b border-slate-100 px-4 py-2.5">
          <SyncStatus active={activeSyncs[store.store_id]} last={store.sync?.last} />
        </div>
      )}
      <div className="grid grid-cols-3 md:grid-cols-1">
        <div className="border-r border-slate-100 px-4 py-3 md:border-r-0 md:border-b">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Toast, this range</p>
          {["gratuity_service_charge", "credit_card_tip", "gift_card_tip", "online_order_tip", "declared_cash_tip"].map((key) => <Money key={key} label={SOURCE_LABEL[key]} cents={store.toast[key] || 0} />)}
          <Money label="Total" cents={store.toast.total} strong />
        </div>
        <div className="border-r border-slate-100 px-4 py-3 md:border-r-0 md:border-b">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Where it went</p>
          {store.pools.map((pool) => (
            <Money key={pool.uuid} label={pool.pool_name} sub={pool.cadence === "daily" ? readable(pool.period_start_date) : `${readable(pool.period_start_date)} to ${readable(pool.period_end_date)}`} cents={sumClasses(pool.pooled)} />
          ))}
          {store.pools.length === 0 && <p className="py-1 text-[13px] text-slate-400">No pool runs yet</p>}
          {/* A destination when it holds money (tips Toast recorded on an
              excluded dining option, returned to the platform); the Toast
              total on the left includes it, so the column ties only with it. */}
          {sumClasses(store.excluded) !== 0 && <Money label="Excluded dining options" cents={sumClasses(store.excluded)} />}
          {/* Under- and over-pooled money shown apart, per class, like the checks: a
              short tips class and an over-full service charge class do not cancel. */}
          {CLASSES.reduce((total, key) => total + Math.max(Number(store.not_pooled?.[key]) || 0, 0), 0) > 0 && <Money label="Outside pool windows" cents={CLASSES.reduce((total, key) => total + Math.max(Number(store.not_pooled?.[key]) || 0, 0), 0)} />}
          {CLASSES.reduce((total, key) => total + Math.max(-(Number(store.not_pooled?.[key]) || 0), 0), 0) > 0 && <Money label="Pooled beyond Toast" cents={CLASSES.reduce((total, key) => total + Math.max(-(Number(store.not_pooled?.[key]) || 0), 0), 0)} />}
          {/* Money rung in by unlinked Toast employees is already inside the
              pool rows above (it is pooled and paid); it is a memo, not a
              destination, so it lives in the tile and the checks, not here. */}
          {sumClasses(store.undistributed_abs || store.undistributed) !== 0 && <Money label="Not distributed" cents={sumClasses(store.undistributed_abs || store.undistributed)} />}
        </div>
        <div className="px-4 py-3">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Checks</p>
          {checksList(shownChecks(store))}
          {store.closeout?.status === "confirmed" && (
            <p className="mt-2 text-xs text-slate-500">Confirmed by {store.closeout.confirmed_by_name || "a manager"}</p>
          )}
        </div>
      </div>
    </section>
  );

  const closeView = () => {
    if (summaryFailed) {
      return (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <div className="text-sm font-semibold text-slate-700">Tips could not be loaded</div>
          <button type="button" onClick={loadSummary} className="mt-2 text-sm font-semibold text-violet-700 hover:text-violet-800">Try again</button>
        </div>
      );
    }
    if (!summary) return <div className="flex justify-center py-24"><LoadSpinner /></div>;
    const totals = summary.totals;
    const tipped = totals.stores - totals.no_policy;
    const count = (status) => summary.stores.filter((store) => store.status === status).length;
    const chips = [
      { key: "all", label: `All ${summary.stores.length}` },
      { key: "needs_attention", label: `Needs attention · ${count("needs_attention")}` },
      { key: "ready", label: `Ready · ${count("ready")}` },
      { key: "confirmed", label: `Confirmed · ${count("confirmed")}` },
      // Shown when any location lacks a policy, and kept while it is the
      // active filter so an empty result still shows what filtered it.
      ...(count("no_policy") || statusFilter === "no_policy" ? [{ key: "no_policy", label: `No policy · ${count("no_policy")}` }] : []),
    ];
    const needle = query.trim().toLowerCase();
    const visibleStores = summary.stores.filter((store) =>
      (statusFilter === "all" || store.status === statusFilter) && (!needle || store.store_name.toLowerCase().includes(needle))
    );
    return (
      <>
        <div className="grid grid-cols-4 gap-3 md:grid-cols-2">
          <Kpi label="Locations confirmed" value={`${totals.confirmed} of ${tipped}`} detail={totals.no_policy ? `${totals.no_policy} without a policy` : "Every location has a policy"} />
          <Kpi label="Pooled this range" value={dollars(sumClasses(totals.pooled))} detail={`Service charges ${dollars(totals.pooled.service_charge)} · tips ${dollars(totals.pooled.tips)}`} />
          <Kpi label="Unlinked collectors" value={dollars(sumClasses(totals.unlinked_pooled || totals.unattributed))} detail="Pooled, but rung in by Toast employees not linked to a person" tone={sumClasses(totals.unlinked_pooled || totals.unattributed) ? "warn" : undefined} />
          <Kpi label="Excluded" value={dollars(sumClasses(totals.excluded))} detail="Third-party delivery and other excluded dining options" />
        </div>
        {summary.stores.length > 0 && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Filter locations by status">
              {chips.map((item) => <button key={item.key} type="button" aria-pressed={statusFilter === item.key} onClick={() => setStatusFilter(item.key)} className={chip(statusFilter === item.key)}>{item.label}</button>)}
            </div>
            <TextField size="small" placeholder="Search locations" value={query} onChange={(event) => setQuery(event.target.value)} inputProps={{ "aria-label": "Search locations" }} InputProps={{ sx: { ...inputSx, height: "36px", width: 240 } }} />
          </div>
        )}
        <div className="mt-3 flex flex-col gap-3">
          {summary.stores.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
              No locations have tip pooling turned on. Turn it on per location under Settings, then open Tip pool policies from the Tip pooling row to assign a policy.
            </div>
          )}
          {summary.stores.length > 0 && visibleStores.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">No locations match.</div>
          )}
          {visibleStores.map(storeCard)}
        </div>
      </>
    );
  };

  // The drill-in's header actions: sync, and confirm or reopen, for the
  // location on screen.
  const breakdownSyncing = Boolean(activeSyncs[breakdownStoreId]);
  const storeActions = breakdown ? (
    <>
      <button type="button" disabled={busy || breakdownSyncing} onClick={() => recompute(breakdown)} className={btnQuiet}><SyncIcon style={{ fontSize: 18 }} className={breakdownSyncing ? "animate-spin" : ""} /><span>{breakdownSyncing ? "Syncing..." : "Sync from Toast"}</span></button>
      <button type="button" onClick={() => { setExportStoreIds([Number(breakdownStoreId)]); setExportOpen(true); }} disabled={!range.start || !range.end} className={btnQuiet}><DownloadIcon style={{ fontSize: 18 }} /><span>Export</span></button>
      {breakdown.status === "confirmed" && (
        <button type="button" disabled={busy || breakdownSyncing} onClick={() => setPendingReopen({ ...breakdown, start_date: datesOf(breakdown).start, end_date: datesOf(breakdown).end })} className={btnOutline}><span>Reopen</span></button>
      )}
      {breakdown.status !== "confirmed" && breakdown.status !== "no_policy" && (
        <button type="button" disabled={busy || breakdownSyncing || breakdown.confirmable === false || Boolean(hardBlock(breakdown))} title={breakdownSyncing ? "Available when the sync finishes" : hardBlock(breakdown)?.text || (breakdown.confirmable === false ? "Only past business days can be confirmed; today's punches and sales are still moving." : undefined)} onClick={() => confirmStore(breakdown)} className={btnPrimary}><CheckIcon style={{ fontSize: 18 }} /><span>Confirm</span></button>
      )}
    </>
  ) : null;

  const breakdownView = () => {
    const current = summary?.stores?.find((store) => store.store_id === Number(breakdownStoreId));
    return (
      <>
        {breakdownFailed && (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
            <div className="text-sm font-semibold text-slate-700">The breakdown could not be loaded</div>
            <button type="button" onClick={() => loadBreakdown()} className="mt-2 text-sm font-semibold text-violet-700 hover:text-violet-800">Try again</button>
          </div>
        )}
        {!breakdown && !breakdownFailed && <div className="flex justify-center py-24"><LoadSpinner /></div>}
        {breakdown && (
          <>
            <SyncStatus active={activeSyncs[breakdown.store_id]} last={breakdown.sync?.last} className="mb-3" />
            <div className="grid grid-cols-6 gap-3 md:grid-cols-2">
              <Kpi label="Toast total" value={dollars(breakdown.toast.total)} detail={`SC ${dollars(breakdown.toast.gratuity_service_charge)} · tips ${dollars(breakdown.toast.credit_card_tip + breakdown.toast.gift_card_tip + (breakdown.toast.online_order_tip || 0))} · cash ${dollars(breakdown.toast.declared_cash_tip)}`} />
              <Kpi label="Pooled" value={dollars(sumClasses(breakdown.pooled))} detail="Tips and service charges in your tip pools" />
              <Kpi label="Excluded" value={dollars(sumClasses(breakdown.excluded))} detail="Excluded dining options" />
              <Kpi label="Not distributed" value={dollars(sumClasses(breakdown.undistributed_abs || breakdown.undistributed))} detail={sumClasses(breakdown.undistributed_abs || breakdown.undistributed) ? "Pooled with nobody eligible" : "Every pooled dollar reached someone"} tone={sumClasses(breakdown.undistributed_abs || breakdown.undistributed) ? "warn" : undefined} />
              <Kpi label="Hours in pools" value={breakdown.totals.hours.toFixed(2)} detail={`${breakdown.open_punches} open ${breakdown.open_punches === 1 ? "punch" : "punches"}`} tone={breakdown.open_punches ? "warn" : undefined} />
              <Kpi label="Take home per hour" value={breakdown.totals.hours ? dollars(Math.round(sumClasses(breakdown.totals.final) / breakdown.totals.hours)) : "–"} detail="Total paid out / total tip-eligible hours" />
            </div>
            {shownChecks(breakdown).some((check) => !check.ok) && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-800">
                <WarningAmberOutlinedIcon style={{ fontSize: 16 }} className="mt-0.5" />
                <div>
                  <span className="font-semibold">{shownChecks(breakdown).filter((check) => !check.ok).length === 1 ? "1 thing" : `${shownChecks(breakdown).filter((check) => !check.ok).length} things`} to look at before confirming.</span>
                  <span className="ml-1">{shownChecks(breakdown).filter((check) => !check.ok).map((check) => check.text).join(" · ")}</span>
                </div>
              </div>
            )}
            <TipBreakdownTable key={`${breakdownStoreId}:${range.start}:${range.end}`} report={breakdown.job_hours}>
            <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="py-3 pl-5 pr-3 text-left text-xs font-bold uppercase text-slate-500">Employee</th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase text-slate-500">Job codes</th>
                    <th className="px-3 py-3 text-right text-xs font-bold uppercase text-slate-500">Hours</th>
                    <th className="px-3 py-3 text-right text-xs font-bold uppercase text-slate-500">Contributed</th>
                    <th className="px-3 py-3 text-right text-xs font-bold uppercase text-slate-500">Received</th>
                    <th className="px-3 py-3 text-right text-xs font-bold uppercase text-slate-500">Net</th>
                    <th className="px-3 py-3 text-right text-xs font-bold uppercase text-slate-500">Service charge take home</th>
                    <th className="px-3 py-3 text-right text-xs font-bold uppercase text-slate-500">Tip take home</th>
                    {hasCash && <th className="px-3 py-3 text-right text-xs font-bold uppercase text-slate-500">Cash take home</th>}
                    {hasAdjustments && <th className="px-3 py-3 text-right text-xs font-bold uppercase text-slate-500">Adjustments</th>}
                    <th className="py-3 pl-3 pr-5 text-right text-xs font-bold uppercase text-slate-500">Take home</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {breakdown.rows.map((row) => {
                    const net = sumClasses(row.net);
                    return (
                      <tr key={row.store_team_member_id} className="cursor-pointer transition-colors hover:bg-slate-50" onClick={() => openPerson(row)}>
                        <td className="whitespace-nowrap py-4 pl-5 pr-3 text-sm font-medium text-slate-900">{formatName(row.name)}{row.archived && <span className="ml-2 text-xs text-slate-400">archived</span>}</td>
                        <td className="px-3 py-4 text-sm"><div className="flex flex-wrap gap-1">{row.roles.map((role) => <span key={role} className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700">{role}</span>)}</div></td>
                        <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-slate-900">{row.hours.toFixed(2)}{row.ineligible_hours > 0 && <IneligibleHoursTooltip hours={row.ineligible_hours} detail={row.ineligible_detail} />}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-slate-500">{sumClasses(row.contributed) ? dollars(sumClasses(row.contributed)) : "–"}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-slate-900">{dollars(sumClasses(row.received))}</td>
                        <td className={`whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums ${net < 0 ? "text-rose-700" : "text-emerald-700"}`}>{dollars(net, { sign: true })}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-slate-900">{dollars(row.received.service_charge)}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-slate-900">{dollars(row.received.tips)}</td>
                        {hasCash && <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-slate-900">{dollars(cashOf(row.received))}</td>}
                        {hasAdjustments && <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-slate-900">{sumClasses(row.adjustments) ? dollars(sumClasses(row.adjustments), { sign: true }) : "–"}</td>}
                        <td className="whitespace-nowrap py-4 pl-3 pr-5 text-right text-sm font-semibold tabular-nums text-slate-900">{dollars(sumClasses(row.final))}</td>
                      </tr>
                    );
                  })}
                  {(breakdown.online_orders || []).map((row) => (
                    <tr key="online-orders" className="bg-slate-50">
                      <td className="whitespace-nowrap py-4 pl-5 pr-3 text-sm"><div className="font-medium text-slate-900">{row.name}</div><div className="text-xs text-slate-500" title="Pickup and platform orders nobody rang in: pooled and paid out, nothing to link">Pooled, nothing to link</div></td>
                      <td className="px-3 py-4 text-sm text-slate-400">–</td>
                      <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>
                      <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-slate-900">{dollars(sumClasses(row.contributed))}</td>
                      <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>
                      <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-slate-500">{dollars(-sumClasses(row.contributed))}</td>
                      <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>
                      <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>
                      {hasCash && <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>}
                      {hasAdjustments && <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>}
                      <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>
                    </tr>
                  ))}
                  {breakdown.unlinked.map((row) => (
                    <tr key={row.collector_external_id} className="bg-amber-50">
                      <td className="whitespace-nowrap py-4 pl-5 pr-3 text-sm"><div className="font-medium text-slate-900">{formatName(row.name)}</div><div className="text-xs text-slate-500">Toast employee not linked to a person</div></td>
                      <td className="px-3 py-4 text-sm"><span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800"><span className="h-1.5 w-1.5 rounded-full bg-amber-600" />Link employee</span></td>
                      <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>
                      <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-slate-900">{dollars(sumClasses(row.contributed))}</td>
                      <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>
                      <td className="whitespace-nowrap px-3 py-4 text-right text-sm tabular-nums text-rose-700">{dollars(-sumClasses(row.contributed))}</td>
                      <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>
                      <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>
                      {hasCash && <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>}
                      {hasAdjustments && <td className="px-3 py-4 text-right text-sm text-slate-400">–</td>}
                      <td className="py-4 pl-3 pr-5 text-right text-sm text-slate-400">–</td>
                    </tr>
                  ))}
                  {breakdown.rows.length === 0 && breakdown.unlinked.length === 0 && (breakdown.online_orders || []).length === 0 && (
                    <tr><td colSpan={10} className="px-5 py-8 text-center text-sm text-slate-500">No pooled tips for this location in this range.</td></tr>
                  )}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr>
                    <td className="py-3 pl-5 pr-3 text-sm font-semibold text-slate-900">Totals</td>
                    <td className="px-3 py-3 text-sm text-slate-500">{breakdown.rows.length} {breakdown.rows.length === 1 ? "person" : "people"}</td>
                    <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums">{breakdown.totals.hours.toFixed(2)}</td>
                    <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums">{dollars(sumClasses(breakdown.totals.contributed))}</td>
                    <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums">{dollars(sumClasses(breakdown.totals.received))}</td>
                    <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums">{dollars(sumClasses(breakdown.totals.received) - sumClasses(breakdown.totals.contributed), { sign: true })}</td>
                    <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums">{dollars(breakdown.totals.received.service_charge)}</td>
                    <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums">{dollars(breakdown.totals.received.tips)}</td>
                    {hasCash && <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums">{dollars(cashOf(breakdown.totals.received))}</td>}
                    {hasAdjustments && <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums">{dollars(sumClasses(breakdown.totals.adjustments), { sign: true })}</td>}
                    <td className="py-3 pl-3 pr-5 text-right text-sm font-semibold tabular-nums">{dollars(sumClasses(breakdown.totals.final))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Net is what a person received minus what they rang in. It sums to zero once every collector is linked and every pooled dollar is distributed; the rows above explain any difference.
              {current?.closeout?.status === "confirmed" && " This range is confirmed; reopen it to change adjustments."}
            </p>
            </TipBreakdownTable>
          </>
        )}
      </>
    );
  };

  // Adjustments are refused on a confirmed range, so the controls hide until it is reopened.
  // Locked per business date: a custom range that only partly overlaps a
  // confirmed close keeps its open days editable, while the days inside the
  // close (which the server refuses to adjust) offer no controls.
  // Each close locks the scope of the runs it owns (reported as scope
  // dates), which can reach before the dates it was asked for.
  const lockedRanges = [
    ...(breakdown?.closeout?.status === "confirmed" ? [{ start_date: breakdown.scope_start_date || breakdown.closeout.start_date, end_date: breakdown.scope_end_date || breakdown.closeout.end_date }] : []),
    ...(breakdown?.overlapping_closeouts || []).map((row) => ({ start_date: row.scope_start_date || row.start_date, end_date: row.scope_end_date || row.end_date })),
  ];
  const isLockedDay = (day) => lockedRanges.some((row) => row.start_date <= day && day <= row.end_date);
  // Days that have not happened at the location yet take no adjustment
  // (the server refuses them): the location's own business date, from the
  // breakdown, bounds the picker and the default.
  const latestOpenDay = breakdown?.today || storeToday;
  const openDays = rangeDays.filter((day) => !isLockedDay(day) && day <= latestOpenDay);
  const adjustmentsLocked = breakdown?.status === "confirmed" || openDays.length === 0;
  const someDaysLocked = !adjustmentsLocked && openDays.length < rangeDays.length;

  const detailDrawer = (
    <Drawer open={Boolean(detail)} anchor="right" onClose={closePerson}>
      {detail && (
        <section aria-label={`${detail.name} tips`} className="relative h-full w-[960px] max-w-full">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader title={detail.name} onClose={closePerson} />
            <div className="w-full px-6">
              {detail.loading && !detailFailed && <div className="flex justify-center py-16"><LoadSpinner /></div>}
              {detailFailed && <p className="text-sm text-slate-500">This person's detail could not be loaded.</p>}
              {!detail.loading && !detailFailed && (
                <>
                  <div className="grid grid-cols-3 gap-2.5">
                    <Kpi label="Take home" value={dollars(sumClasses(detail.totals.final))} detail={`SC ${dollars(detail.totals.final.service_charge)} · tips ${dollars(detail.totals.final.tips)}${cashOf(detail.totals.final) ? ` · cash ${dollars(cashOf(detail.totals.final))}` : ""}${sumClasses(detail.totals.adjustments) ? ` · adjustments ${dollars(sumClasses(detail.totals.adjustments), { sign: true })}` : ""}`} />
                    <Kpi label="Hours in pools" value={detail.totals.hours.toFixed(2)} />
                    <Kpi label="Rang in" value={dollars(sumClasses(detail.totals.contributed))} detail="Contributed to the pools" />
                  </div>
                  <p className="mb-1.5 mt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Pool by pool</p>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-[13px]">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Period</th>
                          <th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Pool</th>
                          <th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Job</th>
                          <th className="px-3 py-2 text-right text-xs font-bold uppercase text-slate-500">Hrs</th>
                          <th className="px-3 py-2 text-right text-xs font-bold uppercase text-slate-500">Pts</th>
                          <th className="px-3 py-2 text-right text-xs font-bold uppercase text-slate-500">Pool total</th>
                          <th className="px-3 py-2 text-right text-xs font-bold uppercase text-slate-500">SC share</th>
                          <th className="px-3 py-2 text-right text-xs font-bold uppercase text-slate-500">Tip share</th>
                          {detailHasCash && <th className="px-3 py-2 text-right text-xs font-bold uppercase text-slate-500">Cash share</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {detail.runs.map((run, index) => (
                          <tr key={`${run.run_uuid}-${index}`}>
                            <td className="whitespace-nowrap px-3 py-2 text-slate-900">{run.period_start_date === run.period_end_date ? readable(run.period_start_date) : `${readable(run.period_start_date)} to ${readable(run.period_end_date)}`}</td>
                            <td className="px-3 py-2"><span className="whitespace-nowrap rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700">{run.pool_name}</span></td>
                            <td className="px-3 py-2 text-slate-500">{run.role_name}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{Number(run.hours).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{String(run.points).replace(/\.00$/, "")}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-500">{dollars(sumClasses(run.pool_total))}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{dollars(run.amounts.service_charge)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{dollars(run.amounts.tips)}</td>
                            {detailHasCash && <td className="px-3 py-2 text-right tabular-nums">{dollars(cashOf(run.amounts))}</td>}
                          </tr>
                        ))}
                        {detail.runs.length === 0 && <tr><td colSpan={detailHasCash ? 9 : 8} className="px-3 py-4 text-center text-slate-500">No pool shares in this range.</td></tr>}
                      </tbody>
                    </table>
                  </div>

                  <p className="mb-1.5 mt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Adjustments</p>
                  <div className="rounded-lg border border-slate-200">
                    {detail.adjustments.map((item) => (
                      <div key={item.uuid} className="flex items-center gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0">
                        <div className="flex-1">
                          <div className="text-sm text-slate-900">{dollars(item.amount_cents, { sign: true })} · {CLASS_LABEL[item.source_class]} · {item.reason}</div>
                          <div className="text-xs text-slate-500">{readable(item.business_date)}{item.created_by_name ? ` · ${item.created_by_name}` : ""}</div>
                        </div>
                        {!adjustmentsLocked && !isLockedDay(item.business_date) && <button type="button" disabled={busy || breakdownSyncing} onClick={() => removeAdjustment(item)} className="text-xs font-medium text-violet-700 hover:underline">Remove</button>}
                      </div>
                    ))}
                    {adjustmentsLocked && (
                      <p className="px-3 py-2.5 text-sm text-slate-500">{detail.adjustments.length === 0 ? "No adjustments. " : ""}This range is confirmed; reopen it to change adjustments.</p>
                    )}
                    {someDaysLocked && (
                      <p className="px-3 py-2 text-xs text-slate-500">Days inside a confirmed close are locked; reopen that close to change them.</p>
                    )}
                    {!adjustmentsLocked && !adjustment && (
                      <button type="button" onClick={() => setAdjustment({ business_date: openDays[openDays.length - 1], source_class: "tips", amount: "", reason: "" })} className="flex w-full items-center gap-1 px-3 py-2.5 text-sm font-medium text-violet-700 hover:bg-slate-50"><AddIcon style={{ fontSize: 16 }} />Add adjustment</button>
                    )}
                    {!adjustmentsLocked && adjustment && (
                      <div className="border-t border-slate-100 bg-slate-50 p-3">
                        <div className="grid grid-cols-3 items-start gap-3">
                          <div>
                            <label id="adjust-date-label" htmlFor="adjust-date" className="mb-1 block text-xs text-slate-500">Day</label>
                            <RightworkSelect id="adjust-date" value={adjustment.business_date} onChange={(event) => setAdjustment((prev) => ({ ...prev, business_date: event.target.value }))} options={openDays.map((day) => ({ value: day, label: readable(day) }))} />
                          </div>
                          <div>
                            <label id="adjust-class-label" htmlFor="adjust-class" className="mb-1 block text-xs text-slate-500">Applies to</label>
                            <RightworkSelect id="adjust-class" value={adjustment.source_class} onChange={(event) => setAdjustment((prev) => ({ ...prev, source_class: event.target.value }))} options={CLASSES.map((key) => ({ value: key, label: CLASS_LABEL[key] }))} />
                          </div>
                          <div>
                            <label htmlFor="adjust-amount" className="mb-1 block text-xs text-slate-500">Amount ($, negative to deduct)</label>
                            <TextField id="adjust-amount" fullWidth size="small" inputProps={{ "aria-label": "Adjustment amount", inputMode: "decimal" }} value={adjustment.amount} onChange={(event) => setAdjustment((prev) => ({ ...prev, amount: event.target.value }))} InputProps={{ sx: inputSx }} />
                          </div>
                        </div>
                        <div className="mt-3">
                          <label htmlFor="adjust-reason" className="mb-1 block text-xs text-slate-500">Reason</label>
                          <TextField id="adjust-reason" fullWidth size="small" inputProps={{ "aria-label": "Adjustment reason" }} value={adjustment.reason} onChange={(event) => setAdjustment((prev) => ({ ...prev, reason: event.target.value }))} InputProps={{ sx: inputSx }} />
                        </div>
                        <div className="mt-2 flex items-center gap-3">
                          <LoadingButton variant="contained" loading={busy} disabled={breakdownSyncing || !adjustment.reason.trim() || !Number(adjustment.amount)} onClick={saveAdjustment} sx={{ textTransform: "none", borderRadius: "8px", backgroundColor: "#6d28d9", "&:hover": { backgroundColor: "#5b21b6" } }}>Add adjustment</LoadingButton>
                          <button type="button" onClick={() => setAdjustment(null)} className="text-sm font-medium text-slate-600 hover:underline">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Adjustments are added on top of the computed share and never change anyone else's amount.</p>
                </>
              )}
            </div>
            <div className="h-[120px]" />
          </div>
          <DrawerFooterContainerSmall>
            <div className="flex h-full w-full items-center">
              <div className="w-1/2 pr-2"><ButtonOutlined text="Cancel" onClick={closePerson} /></div>
              <div className="w-1/2 pl-2"><ButtonFilled text="Done" onClick={closePerson} /></div>
            </div>
          </DrawerFooterContainerSmall>
        </section>
      )}
    </Drawer>
  );

  // The heading names the dates the view actually covers: a breakdown of a
  // location whose week differs from the home location's shows its own, and
  // the scope a longer run widened it to (Wednesday to Sunday asked for, the
  // Monday to Sunday weekly run it owns reviewed and confirmed), with the
  // dates asked for alongside so the widening is never silent.
  const requestedRange = view === "breakdown" && breakdown ? datesOf(breakdown) : null;
  const shownRange = requestedRange
    ? { start: breakdown.scope_start_date || requestedRange.start, end: breakdown.scope_end_date || requestedRange.end }
    : range;
  const widened = Boolean(requestedRange) && (requestedRange.start !== shownRange.start || requestedRange.end !== shownRange.end);
  const openStore = view === "breakdown"
    ? (summary?.stores?.find((store) => store.store_id === Number(breakdownStoreId)) || tipStores.find((store) => Number(store.id) === Number(breakdownStoreId)))
    : null;
  const openStoreName = openStore?.store_name || breakdown?.store_name || "Location";

  return (
    <div className="min-h-screen bg-white">
      {/* The company-scoped page header Tickets and Contests use: eyebrow,
          title, company dropdown, account. Tips is a company view, so there
          is no location picker or per-location notification bell here. */}
      <div className="my-5 pb-16">
        {/* Overview is the page. A location card drills into its breakdown,
            whose header names the location and the dates it covers, with a
            back arrow, like a prebuilt report opened from Reporting. */}
        <OperationsPageHeader
          title={view === "close" ? "Tips" : openStoreName}
          eyebrow={view === "close" ? "Time & Payroll" : null}
          onBack={view === "close" ? null : backToOverview}
          titleBadge={view === "breakdown" && breakdown ? <Badge status={breakdown.status} /> : null}
          scope="company"
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          eligibleCompanyIds={tipCompanyIds}
          change_home_store={props.change_home_store}
        >
          {/* The controls sit on their own wrapping row under the title, where
              Tickets keeps its buttons, so a long location name and the
              company dropdown never fight them for the header row. */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              {rangeSelect}
              {customPickers}
              {/* A location resolves the preset to its own business week, which
                  can differ from the home location's shown in the select; only
                  then are the dates its actions apply to spelled out. */}
              {view === "breakdown" && breakdown && shownRange.start && shownRange.end && (shownRange.start !== range.start || shownRange.end !== range.end) && (
                <span className="text-sm text-slate-500">This location's week: {readable(shownRange.start)} to {readable(shownRange.end)}</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {view === "close" ? exportButton : storeActions}
            </div>
          </div>
          {widened && (
            <p className="mt-3 text-xs text-amber-700">Asked for {readable(requestedRange.start)} to {readable(requestedRange.end)}; a longer pool run widened it to the whole period.</p>
          )}
          <div className="mt-6">{view === "close" ? closeView() : breakdownView()}</div>
        </OperationsPageHeader>
      </div>
      {detailDrawer}
      {exportOpen && <TipsExportModal open={exportOpen} onClose={() => setExportOpen(false)} companyId={companyId} range={range} preset={preset}
        stores={(summary?.stores || []).map((store) => ({ store_id: store.store_id, store_name: store.store_name }))}
        initialStoreIds={exportStoreIds}
      />}
      <ConfirmDialog
        open={Boolean(pendingReopen)}
        title={pendingReopen ? `Reopen ${pendingReopen.store_name} for ${readable(pendingReopen.start_date)} to ${readable(pendingReopen.end_date)}?` : ""}
        message="The confirmed runs go back to draft and are recomputed from the latest Toast data. Confirm again when it looks right."
        confirmLabel="Reopen"
        tone="primary"
        onConfirm={reopenStore}
        onCancel={() => setPendingReopen(null)}
      />
    </div>
  );
}
